import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecommendShowModal = ({ show, onClose }) => {
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadFriends();
  }, []);

  const loadFriends = async () => {
    try {
      const response = await axios.get(`${API}/follows/following`, {
        withCredentials: true
      });
      setFriends(response.data || []);
    } catch (error) {
      console.error('Error loading friends:', error);
      toast.error('Error loading friends list');
    } finally {
      setLoadingFriends(false);
    }
  };

  const handleSendRecommendation = async () => {
    if (!selectedFriend) {
      toast.error('Please select a friend');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/notifications/recommend`,
        {
          friend_id: selectedFriend.id,
          show_id: show.id,
          show_title: show.title,
          show_image_url: show.image_url,
          message: message.trim() || null
        },
        { withCredentials: true }
      );

      toast.success(`Recommendation sent to ${selectedFriend.name}!`);
      onClose();
    } catch (error) {
      console.error('Error sending recommendation:', error);
      toast.error(error.response?.data?.detail || 'Error sending recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="recommend-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Recommend "{show.title}" to a Friend</DialogTitle>
        </DialogHeader>

        <div className="modal-body">
          {loadingFriends ? (
            <div className="loading">Loading friends...</div>
          ) : friends.length === 0 ? (
            <div className="empty-state">
              <p>You need to follow friends before you can send recommendations.</p>
              <p style={{ fontSize: '0.85rem', color: '#b0b0b0', marginTop: '1rem' }}>
                Go to the <strong>Friends</strong> page to search for and follow other users.
              </p>
              <Button 
                onClick={() => window.location.href = '/friends'}
                style={{ marginTop: '1rem', background: '#E62F88', color: '#000', borderRadius: '8px' }}
              >
                Find Friends
              </Button>
            </div>
          ) : (
            <>
              <div className="section">
                <label className="section-label">Select a friend:</label>
                <div className="friend-list-container">
                  <label className="dropdown-label">
                    Select from your friends ({friends.length}):
                  </label>
                  
                  <input
                    type="text"
                    className="friend-search"
                    placeholder="Search friends by name or username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />

                  <div className="friends-list-scroll">
                    {friends
                      .filter(friend => {
                        const query = searchQuery.toLowerCase();
                        return friend.name.toLowerCase().includes(query) || 
                               friend.username.toLowerCase().includes(query);
                      })
                      .map((friend) => (
                        <div 
                          key={friend.id}
                          className={`friend-item ${selectedFriend?.id === friend.id ? 'selected' : ''}`}
                          onClick={() => setSelectedFriend(friend)}
                        >
                          <Avatar className="friend-avatar">
                            <AvatarImage src={friend.picture} alt={friend.name} />
                            <AvatarFallback>{friend.name?.charAt(0) || 'U'}</AvatarFallback>
                          </Avatar>
                          <div className="friend-info">
                            <div className="friend-name">{friend.name}</div>
                            <div className="friend-username">@{friend.username}</div>
                          </div>
                          {selectedFriend?.id === friend.id && (
                            <div className="selected-indicator">✓</div>
                          )}
                        </div>
                      ))}
                    
                    {friends.filter(friend => {
                      const query = searchQuery.toLowerCase();
                      return friend.name.toLowerCase().includes(query) || 
                             friend.username.toLowerCase().includes(query);
                    }).length === 0 && (
                      <div className="no-results">
                        No friends found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="section">
                <label className="section-label">Personal message (optional):</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell them why they'll love this show..."
                  rows={3}
                  maxLength={200}
                  className="message-input"
                />
                <div className="char-count">{message.length}/200</div>
              </div>

              <div className="modal-actions">
                <Button
                  onClick={handleSendRecommendation}
                  disabled={loading || !selectedFriend}
                  className="send-btn"
                >
                  {loading ? 'Sending...' : 'Send Recommendation'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>

        <style jsx>{`
          .recommend-modal {
            max-width: 500px;
          }

          .recommend-modal :global(button[class*="absolute"]) {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .recommend-modal :global(button[class*="absolute"]:hover) {
            color: #ffffff !important;
            background: transparent !important;
          }

          .recommend-modal :global(button[class*="absolute"]:focus) {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .modal-body {
            padding: 1rem 0;
          }

          .loading,
          .empty-state {
            text-align: center;
            padding: 2rem;
            color: #b0b0b0;
          }

          .section {
            margin-bottom: 1.5rem;
          }

          .section-label {
            display: block;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.75rem;
            font-size: 0.95rem;
          }

          .friend-list-container {
            width: 100%;
          }

          .dropdown-label {
            display: block;
            font-size: 0.9rem;
            color: #b0b0b0;
            margin-bottom: 0.75rem;
          }

          .friend-search {
            width: 100%;
            padding: 0.75rem;
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            color: #ffffff;
            font-size: 0.95rem;
            margin-bottom: 1rem;
            outline: none;
            transition: border-color 0.2s;
          }

          .friend-search:focus {
            border-color: #E62F88;
          }

          .friend-search::placeholder {
            color: #666;
          }

          .friends-list-scroll {
            max-height: 300px;
            overflow-y: auto;
            padding-right: 0.5rem;
          }

          .friends-list-scroll::-webkit-scrollbar {
            width: 6px;
          }

          .friends-list-scroll::-webkit-scrollbar-track {
            background: #1a1a1a;
            border-radius: 3px;
          }

          .friends-list-scroll::-webkit-scrollbar-thumb {
            background: #333;
            border-radius: 3px;
          }

          .friends-list-scroll::-webkit-scrollbar-thumb:hover {
            background: #444;
          }

          .no-results {
            text-align: center;
            padding: 2rem;
            color: #666;
            font-size: 0.9rem;
          }

          .friend-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem;
            background: #1a1a1a;
            border: 2px solid #333;
            border-radius: 8px;
            margin-bottom: 0.5rem;
            cursor: pointer;
            transition: all 0.2s;
          }

          .friend-item:hover {
            border-color: #E62F88;
            background: #222;
          }

          .friend-item.selected {
            border-color: #E62F88;
            background: #2a1a24;
          }

          .friend-avatar {
            width: 40px;
            height: 40px;
            flex-shrink: 0;
          }

          .friend-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .friend-name {
            color: #ffffff;
            font-weight: 600;
            font-size: 0.95rem;
          }

          .friend-username {
            color: #b0b0b0;
            font-size: 0.85rem;
          }

          .selected-indicator {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #E62F88;
            color: #000;
            border-radius: 50%;
            font-weight: bold;
            flex-shrink: 0;
          }

          .message-input {
            width: 100%;
            background: #1a1a1a;
            border: 1px solid #333;
            color: #ffffff;
            resize: vertical;
          }

          .char-count {
            text-align: right;
            font-size: 0.85rem;
            color: #b0b0b0;
            margin-top: 0.25rem;
          }

          .modal-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            margin-top: 1.5rem;
          }

          .send-btn {
            background: #E62F88;
            color: #000000;
          }

          .send-btn:hover:not(:disabled) {
            background: #E48BB6;
          }

          .send-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default RecommendShowModal;
